import React, { Component } from 'react'
import PropTypes from 'prop-types'
import sortBy from 'sort-by'
import { Table, Column, Menu, Dropdown, Icon, Spin } from './common/Ant'
import { Anchor, ActionAnchor } from './common/Anchor'
import Select, { Option } from './common/Select'
import InputContainer from '../containers/common/InputContainer'
import { hasPermission } from '../utils/hasPermission'
import { createQueryString, parseQueryParams } from '../utils/queryParams'
import {
  COUNTRIES,
  MODEM_COUNTRIES,
  DEFAULT_PAGE,
  DEFAULT_PER_PAGE,
  VIEW_ALL,
  USER_PERMISSIONS,
} from '../constants'
import { configurePagination, escapeRegExp } from '../utils/helpers'

const formatCountry = code => COUNTRIES[code] || code

const formatList = (values, formatter) =>
  values && values.length ? values.map(formatter).join(', ') : '--'

class AllModemTypes extends Component {
  constructor(props) {
    super(props)

    this.state = {
      sortedInfo: {},
      data: this.props.modemTypes,
      searchText: '',
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
      carrierId: undefined,
      country: undefined,
    }
  }

  static propTypes = {
    modemTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
    carriers: PropTypes.arrayOf(PropTypes.object).isRequired,
    getAllCarriers: PropTypes.func,
    windowWidth: PropTypes.number.isRequired,
    meta: PropTypes.object,
    getAllModemTypes: PropTypes.func,
    deleteModemType: PropTypes.func,
    updateQueryParams: PropTypes.func,
    isModemTypeLoading: PropTypes.bool,
  }

  componentWillReceiveProps(nextProps) {
    this.handleSearch()
  }

  componentDidMount() {
    const { page, perPage, carrierId, country } = parseQueryParams(
      global.location
    )
    const { getAllModemTypes, getAllCarriers } = this.props
    const query = {
      page: page ? parseInt(page, 10) : DEFAULT_PAGE,
      perPage: perPage ? parseInt(perPage, 10) : DEFAULT_PER_PAGE,
      carrierId,
      country,
    }
    this.setState(query)
    getAllModemTypes && getAllModemTypes(createQueryString(query))
    // every carrier, for the carrier filter
    getAllCarriers && getAllCarriers(createQueryString({ perPage: 'all' }))
  }

  handleChange = (pagination, filters, sorter) => {
    const { getAllModemTypes, updateQueryParams } = this.props
    const { page, perPage, carrierId, country } = this.state

    if (page !== pagination.current) {
      const query = { page: pagination.current, perPage, carrierId, country }

      const qs = createQueryString(query)
      getAllModemTypes && getAllModemTypes(qs)

      updateQueryParams && updateQueryParams({ search: qs })
    }

    this.setState({
      page: pagination.current,
      sortedInfo: sorter,
      searchText: '',
    })
  }

  handleFilterChange = keyName => value => {
    const { getAllModemTypes, updateQueryParams } = this.props
    const { perPage, carrierId, country } = this.state

    const newValue =
      value === undefined || (value.length && value.indexOf('all') > -1)
        ? undefined
        : value

    // a new filter can change the number of results, so go back to the
    // first page so the API doesn't return a 404
    const query = Object.assign(
      { page: DEFAULT_PAGE, perPage, carrierId, country },
      { [keyName]: newValue }
    )

    this.setState({ [keyName]: newValue, page: DEFAULT_PAGE })

    const qs = createQueryString(query)
    getAllModemTypes(qs)

    updateQueryParams && updateQueryParams({ search: qs })
  }

  handleInputChange = e => {
    this.setState({ searchText: e.currentTarget.value }, () =>
      this.handleSearch()
    )
  }

  handleSearch = () => {
    const { searchText } = this.state
    const regex = new RegExp(escapeRegExp(searchText), 'gi')

    this.setState((state, props) => ({
      data: props.modemTypes.filter(
        record => record.name && record.name.match(regex)
      ),
    }))
  }

  render() {
    const {
      deleteModemType,
      carriers,
      meta,
      windowWidth,
      isModemTypeLoading,
    } = this.props
    const {
      data,
      sortedInfo,
      searchText,
      perPage,
      carrierId,
      country,
    } = this.state

    const isFixedColumn = windowWidth < 750
    const isFiltering = !!(
      (carrierId && carrierId.length) ||
      (country && country.length)
    )
    const canEdit = hasPermission(USER_PERMISSIONS.EDIT_MODEM_AND_CARRIER)
    const canDelete = hasPermission(USER_PERMISSIONS.DELETE_MODEM_AND_CARRIER)

    return (
      <section className="mb5 AllModemTypes">
        {hasPermission(USER_PERMISSIONS.ADD_MODEM_AND_CARRIER) && (
          <div>
            <Anchor to="/inventory/modem-types/create" className="mb3" button>
              Create New
            </Anchor>
            <div className="dib red">
              {isFiltering && (
                <p className="pl3">
                  Found {meta.total} modem types matching your criteria
                </p>
              )}
            </div>
          </div>
        )}
        <div className="flex-l items-center-l">
          <Select
            className="w-50-l mr3-l"
            placeholder="Carrier"
            mode="multiple"
            input={{
              value: carrierId,
              onChange: this.handleFilterChange('carrierId'),
            }}
            filterable
          >
            {[{ id: 'all', name: VIEW_ALL }, ...carriers].map(x => (
              <Option value={`${x.id}`} key={x.id}>
                {x.name}
              </Option>
            ))}
          </Select>
          <Select
            className="w-50-l"
            placeholder="Country"
            mode="multiple"
            input={{
              value: country,
              onChange: this.handleFilterChange('country'),
            }}
            filterable
          >
            {['all', ...MODEM_COUNTRIES].map(x => (
              <Option value={x} key={x}>
                {x === 'all' ? VIEW_ALL : formatCountry(x)}
              </Option>
            ))}
          </Select>
        </div>
        <div className="table-filter">
          <InputContainer
            input={{
              placeholder: 'Filter',
              value: searchText,
              onChange: this.handleInputChange,
            }}
          />
        </div>
        {isModemTypeLoading ? (
          <Spin size="large" className="w-100 center mt5" />
        ) : (
          <Table
            dataSource={data}
            onChange={this.handleChange}
            rowKey="id"
            scroll={{ x: 650 }}
            bordered
            pagination={configurePagination({
              perPage,
              onShowSizeChange: (page, perPage) =>
                this.handleFilterChange('perPage')(perPage),
              ...meta,
            })}
            tableLayout="auto"
          >
            <Column
              title="Name"
              dataIndex="name"
              sorter={sortBy('name')}
              sortOrder={sortedInfo.field === 'name' && sortedInfo.order}
              width={200}
              render={text => (text ? text : '--')}
            />
            <Column
              title="Carriers"
              dataIndex="carriers"
              width={250}
              render={carriers => formatList(carriers, x => x.name)}
            />
            <Column
              title="Countries"
              dataIndex="countries"
              width={200}
              render={countries => formatList(countries, formatCountry)}
            />
            {(canEdit || canDelete) && (
              <Column
                fixed={isFixedColumn ? 'right' : false}
                width={100}
                render={(text, record) => {
                  const menu = (
                    <Menu>
                      {canEdit && (
                        <Menu.Item key="edit-modem-type">
                          <Anchor
                            to={`/inventory/modem-types/${record.id}/edit`}
                          >
                            Edit
                          </Anchor>
                        </Menu.Item>
                      )}
                      {canDelete && (
                        <Menu.Item key="delete-modem-type">
                          <ActionAnchor
                            onClick={() => deleteModemType(record.id)}
                          >
                            Delete
                          </ActionAnchor>
                        </Menu.Item>
                      )}
                    </Menu>
                  )

                  return (
                    <Dropdown overlay={menu} trigger={['click']}>
                      <a className="ant-dropdown-link">
                        Actions <Icon type="down" />
                      </a>
                    </Dropdown>
                  )
                }}
              />
            )}
          </Table>
        )}
      </section>
    )
  }
}

export default AllModemTypes
